import { Link, useParams } from 'react-router-dom';
import type { Race } from '../types';
import { useRaceData } from '../hooks';
import { useUserRaces } from '../context/UserRacesContext';
import { useAuth } from '../auth/AuthContext';
import { BookmarkButton } from '../races/BookmarkButton';
import { RegistrationAction } from '../races/RegistrationAction';
import { formatKorDate } from '../utils/dateRange';
import { getRegionLabel } from '../utils/regionMapper';
import { STATUS_LABELS } from '../utils/raceLabels';
import { formatDistance, sortDistances } from '../utils/distance';
import './RaceDetailPage.css';

function findRace(races: Race[], id: string): Race | null {
  return races.find((r) => r.id === id) ?? null;
}

export function RaceDetailPage() {
  const { lang, id } = useParams<{ lang?: string; id: string }>();
  const { races, loading, error } = useRaceData();
  const { user } = useAuth();
  const { bookmarkedIds, registeredIds, finishedIds } = useUserRaces();
  const langPrefix = lang ? `/${lang}` : '';

  if (loading) {
    return (
      <div className="race-detail-page">
        <div className="race-detail-loading">불러오는 중…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="race-detail-page">
        <Link to={`${langPrefix}/`} className="race-detail-back">
          ← 목록으로
        </Link>
        <div className="race-detail-error">대회 정보를 불러오지 못했습니다.</div>
      </div>
    );
  }

  const race = id ? findRace(races, id) : null;

  if (!race) {
    return (
      <div className="race-detail-page">
        <Link to={`${langPrefix}/`} className="race-detail-back">
          ← 목록으로
        </Link>
        <div className="race-detail-error">해당 대회를 찾을 수 없습니다.</div>
      </div>
    );
  }

  const distances = sortDistances(race.distances);
  const website = race.website ?? race.registrationUrl ?? race.sourceUrl;
  const isBookmarked = user && bookmarkedIds.has(race.id);
  const isRegistered = user && registeredIds.has(race.id);
  const isFinished = user && finishedIds.has(race.id);

  return (
    <div className="race-detail-page">
      <Link to={`${langPrefix}/`} className="race-detail-back">
        ← 목록으로
      </Link>

      <div className="race-detail-header">
        <div>
          <h1 className="race-detail-title">{race.nameLocal || race.name}</h1>
          {race.nameLocal && race.name !== race.nameLocal && (
            <div className="race-detail-subtitle">{race.name}</div>
          )}
        </div>
        <BookmarkButton raceId={race.id} raceName={race.nameLocal || race.name} />
      </div>

      {(isBookmarked || isRegistered || isFinished) && (
        <div className="race-detail-status-chips">
          {isBookmarked && (
            <span className="race-detail-chip is-bookmarked">★ 북마크됨</span>
          )}
          {isRegistered && (
            <span className="race-detail-chip is-registered">✓ 참가 등록</span>
          )}
          {isFinished && (
            <span className="race-detail-chip is-finished">● 완주 완료</span>
          )}
        </div>
      )}

      <dl className="race-detail-meta">
        <div className="race-detail-meta-row">
          <dt>대회일</dt>
          <dd>{formatKorDate(race.date, 'full')}</dd>
        </div>
        <div className="race-detail-meta-row">
          <dt>지역</dt>
          <dd>{getRegionLabel(race.region)}</dd>
        </div>
        <div className="race-detail-meta-row">
          <dt>상태</dt>
          <dd>{STATUS_LABELS[race.status]}</dd>
        </div>
        <div className="race-detail-meta-row">
          <dt>거리</dt>
          <dd>{distances.map((d) => formatDistance(d)).join(' · ')}</dd>
        </div>
      </dl>

      <div className="race-detail-actions">
        <RegistrationAction race={race} />
        {website && (
          <a
            href={website}
            className="race-detail-external"
            target="_blank"
            rel="noopener noreferrer"
          >
            대회 사이트로 →
          </a>
        )}
      </div>
    </div>
  );
}
